import React, { useState } from 'react';
import { X, ShoppingCart, Heart, Star } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatPrice, calculateDiscount } from '../../lib/utils';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { Link } from 'react-router-dom';

export const QuickViewModal = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();

  if (!isOpen || !product) return null;

  const finalPrice = product.discount > 0
    ? calculateDiscount(product.price, product.discount)
    : product.price;

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <Card 
        className="max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl font-bold">Quick View</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Image */}
            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
              {product.images && product.images[0] ? (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-4xl text-muted-foreground">NC</span>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col">
              <p className="text-sm text-muted-foreground mb-2">{product.brand}</p>
              <h3 className="font-serif text-3xl font-bold mb-3">{product.name}</h3>
              
              <div className="flex items-center gap-3 mb-4">
                {product.discount > 0 && (
                  <Badge variant="destructive">Save {product.discount}%</Badge>
                )}
                {product.stock_quantity > 0 ? (
                  <Badge variant="success">In Stock</Badge>
                ) : (
                  <Badge variant="destructive">Out of Stock</Badge>
                )}
              </div>

              {product.rating && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="font-semibold">{product.rating}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    ({product.reviews_count} reviews)
                  </span>
                </div>
              )}

              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-3xl font-bold text-primary">
                    {formatPrice(finalPrice)}
                  </span>
                  {product.discount > 0 && (
                    <span className="text-xl text-muted-foreground line-through">
                      {formatPrice(product.price)}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-muted-foreground mb-6 leading-relaxed">
                {product.description}
              </p>

              <div className="flex gap-3 mt-auto">
                <Button
                  className="flex-1"
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity <= 0}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Cart
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => addToWishlist(product)}
                >
                  <Heart className={`w-5 h-5 ${isInWishlist(product.id) ? 'fill-primary text-primary' : ''}`} />
                </Button>
              </div>

              <Link to={`/product/${product.id}`} className="mt-4">
                <Button variant="ghost" className="w-full">
                  View Full Details
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};